import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Label } from "../../ui/label";
import { useWorkflowStore } from "../../../store/useWorkflowStore";
import { useConnectedServicesStore } from "../../../store/useConnectedServicesStore";

/**
 * InstanceSelector component
 * 
 * Dropdown for selecting a connected service instance
 */
interface InstanceSelectorProps {
  isTarget?: boolean;
  label?: string;
}

export const InstanceSelector: React.FC<InstanceSelectorProps> = ({ 
  isTarget = false,
  label = "Instance"
}) => {
  const { updateFormField } = useWorkflowStore();
  const { services: connectedServices, fetchDatabases } = useConnectedServicesStore();

  // Field name based on whether this is for source or target
  const fieldName = isTarget ? "targetHost" : "host";
  const placeholderText = isTarget ? "Select instance" : "Select source instance";

  // Handle instance selection change
  const handleInstanceChange = (value: string) => {
    if (
      value === "loading-state" ||
      value === "error-state" ||
      value === "no-instances-state"
    ) {
      return;
    }

    updateFormField(fieldName, value);

    if (value && Array.isArray(connectedServices)) {
      fetchDatabases(parseInt(value));
    }
  };

  return (
    <div>
      <div className="grid w-full items-center gap-1.5">
        <Label htmlFor={fieldName}>{label}</Label>
        <Select onValueChange={handleInstanceChange}>
          <SelectTrigger id={fieldName} className="w-full">
            <SelectValue placeholder={placeholderText} />
          </SelectTrigger>
          <SelectContent>
            {!connectedServices || !Array.isArray(connectedServices) ? (
              <SelectItem value="loading-state" disabled>
                Loading instances...
              </SelectItem>
            ) : connectedServices.length === 0 ? (
              <SelectItem value="no-instances-state" disabled>
                No instances available
              </SelectItem>
            ) : (
              connectedServices.map((service) => (
                <SelectItem key={service.id} value={service.id.toString()}>
                  {service.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default InstanceSelector;
